/**
 * Fonte única da navegação do app (menu gaveta, sidebar e atalhos da Home).
 * Agrupa pelo ciclo de trabalho da fazenda — não por lista plana de telas.
 *
 * `icon` é uma chave do registro em `components/ui/Icon.js`, não um emoji. O
 * significado fica aqui e o desenho fica lá: trocar o símbolo de uma rota é
 * mexer numa linha do registro, sem caçar glifo espalhado pelas telas.
 */

export const NAV_SECTIONS = [
  {
    id: 'safra',
    label: 'Safra',
    items: [
      { to: '/safras', label: 'Safras', icon: 'safra', desc: 'Ciclos e áreas plantadas' },
      {
        to: '/custos',
        label: 'Manejo',
        icon: 'manejo',
        desc: 'Aplicações e tratos na lavoura',
      },
      { to: '/insumos', label: 'Insumos', icon: 'insumo', desc: 'Compras e estoque de insumos' },
    ],
  },
  {
    id: 'producao',
    label: 'Produção',
    items: [
      { to: '/cargas', label: 'Colheita', icon: 'colheita', desc: 'Registrar produção' },
      {
        to: '/vendas?aba=estoque',
        label: 'Estoque de produção',
        icon: 'estoque',
        pathMatch: '/vendas',
        desc: 'Saldo por armazém e safra',
      },
      { to: '/armazens', label: 'Armazéns', icon: 'armazem', desc: 'Locais de armazenagem' },
      { to: '/vendas', label: 'Vendas', icon: 'venda', desc: 'Registrar comercialização' },
    ],
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    items: [
      { to: '/resultado', label: 'Resultado', icon: 'resultado', desc: 'Lucro, custos e produtividade' },
      { to: '/relatorios/producao', label: 'Produção', icon: 'producao', desc: 'Cargas por período e armazém' },
      { to: '/relatorios/comparar-areas', label: 'Comparar áreas', icon: 'comparar-areas', desc: 'Talhão × talhão na mesma safra' },
      { to: '/relatorios/comparar-safras', label: 'Comparar safras', icon: 'comparar-safras', desc: 'Safras da mesma cultura' },
      { to: '/relatorios/custos-lavoura', label: 'Custos por lavoura', icon: 'manejo', desc: 'Gastos e aplicações por área' },
    ],
  },
  {
    id: 'propriedade',
    label: 'Propriedade',
    items: [
      { to: '/lavouras', label: 'Áreas de cultivo', icon: 'lavoura', desc: 'Lavouras e estufas' },
      { to: '/maquinario', label: 'Maquinário', icon: 'maquinario', desc: 'Equipamentos e manutenções' },
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    items: [
      { to: '/livro-caixa', label: 'Livro Caixa', icon: 'livro-caixa', desc: 'Extrato para o contador' },
      {
        to: '/contas-a-pagar',
        label: 'Contas a pagar',
        icon: 'conta-pagar',
        desc: 'Vencimentos e avisos',
      },
      {
        to: '/contas-a-receber',
        label: 'Contas a receber',
        icon: 'conta-receber',
        desc: 'Recebimentos a prazo',
      },
    ],
  },
  {
    id: 'conta',
    label: 'Conta',
    items: [
      { to: '/minha-assinatura', label: 'Minha Assinatura', icon: 'assinatura', desc: 'Plano e pagamento' },
      {
        to: '/usuarios',
        label: 'Usuários',
        icon: 'usuarios',
        desc: 'Equipe da fazenda',
        adminOnly: true,
      },
      { to: '/notificacoes', label: 'Notificações', icon: 'notificacao', desc: 'Avisos e pendências' },
      {
        to: '/admin',
        label: 'Administração',
        icon: 'usuarios',
        desc: 'Painel interno da plataforma',
        masterOnly: true,
      },
    ],
  },
];

/** Atalhos da Home — menos cards, alinhados às macros. */
export const HOME_ATALHOS = [
  {
    titulo: 'No dia a dia',
    descricao: 'O que você registra com mais frequência',
    itens: [
      { to: '/cargas', icon: 'colheita', label: 'Colheita', desc: 'Registrar produção' },
      { to: '/custos', icon: 'manejo', label: 'Manejo', desc: 'Aplicar na lavoura' },
      { to: '/vendas', icon: 'venda', label: 'Vendas', desc: 'Registrar venda' },
      { to: '/insumos', icon: 'insumo', label: 'Insumos', desc: 'Comprar e estoque' },
    ],
  },
  {
    titulo: 'Acompanhar',
    descricao: 'Resultado e saldos',
    itens: [
      { to: '/resultado', icon: 'resultado', label: 'Resultado', desc: 'Lucro por safra' },
      { to: '/relatorios', icon: 'relatorio', label: 'Relatórios', desc: 'Produção e comparações' },
      {
        to: '/vendas?aba=estoque',
        icon: 'estoque',
        label: 'Estoque produção',
        desc: 'Saldo colhido',
      },
      { to: '/livro-caixa', icon: 'livro-caixa', label: 'Livro Caixa', desc: 'Exportar IRPF' },
    ],
  },
  {
    titulo: 'Cadastros',
    descricao: 'Base da fazenda',
    itens: [
      { to: '/safras', icon: 'safra', label: 'Safras', desc: 'Ciclos produtivos' },
      { to: '/lavouras', icon: 'lavoura', label: 'Áreas', desc: 'Lavouras e estufas' },
      { to: '/maquinario', icon: 'maquinario', label: 'Maquinário', desc: 'Equipamentos' },
    ],
  },
];

/** Pathname da rota, ignorando query (ex.: /vendas?aba=estoque → /vendas). */
export function pathFromTo(to) {
  return (to || '').split('?')[0] || '/';
}

/**
 * Item ativo: match exato do path, ou pathMatch explícito.
 * Estoque de produção e Vendas compartilham /vendas — só marca estoque quando
 * a query aba=estoque está presente; caso contrário marca Vendas.
 */
export function isNavItemActive(item, pathname, search = '') {
  const itemPath = item.pathMatch || pathFromTo(item.to);
  if (pathname !== itemPath) return false;

  const querEstoque = item.to.includes('aba=estoque');
  const urlTemEstoque = new URLSearchParams(search).get('aba') === 'estoque';

  if (itemPath === '/vendas') {
    return querEstoque ? urlTemEstoque : !urlTemEstoque;
  }
  return true;
}

export function sectionContainsPath(section, pathname) {
  // O hub /relatorios não é um item do menu (os destinos já estão listados),
  // mas a seção precisa abrir quando a pessoa chega nele pela Home.
  if (section.id === 'relatorios' && pathname === '/relatorios') return true;
  return section.items.some(
    (item) => (item.pathMatch || pathFromTo(item.to)) === pathname
  );
}

/**
 * Filtra os itens pelo que a conta pode ver.
 *
 * `adminOnly` depende do papel ADMIN na fazenda ativa; `masterOnly` depende de
 * ser o dono da plataforma (decidido pelo backend, não pelo papel na fazenda) —
 * por isso são dois critérios separados.
 */
export function filterNavForRole(sections, isAdmin, isMaster = false) {
  return sections
    .map((s) => ({
      ...s,
      items: s.items.filter((i) =>
        (!i.adminOnly || isAdmin) && (!i.masterOnly || isMaster)),
    }))
    .filter((s) => s.items.length > 0);
}
